import { useSendDocuments } from '@cannect/hooks/useSendDocuments/useSendDocuments'
import { Result } from 'antd'
import { NoToken } from 'assets/svg/result'
import Button from 'new-components/Button'
import { useMemo } from 'react'
import { SendAnvisaDocumentUI } from '../../../../../../SendAnvisaDocument/sendAnvisaDocumentUI'
import { SendImportDocumentsUI } from '../../../../../../SendImportDocuments/sendImportDocumentsUI'
import { SendLaudoMedicoUI } from '../../../../../../SendLaudoMedico/sendLaudoMedicoUI'
import { SendPrescripitonDocUI } from '../../../../../../SendPrescripitonDoc/sendPrescriptionDocUI'
import { CartContainer, CartHeader, CartTitle } from '../../styles'

export type SendDocumentsStep = 'receita' | 'pessoais' | 'laudo' | 'anvisa'

const STEP_TITLE: Record<SendDocumentsStep, string> = {
  receita: 'Envio de Receita',
  pessoais: 'Envio de Documentos',
  laudo: 'Laudo Médico',
  anvisa: 'Autorização ANVISA'
}

interface SendDocumentsProps {
  orderId: number | string | null
  step: SendDocumentsStep
  /** Navega para outra etapa do fluxo dentro do painel de associados */
  onNavigateToStep: (step: SendDocumentsStep) => void
  /** Fim do fluxo — detalhe do pedido */
  onComplete: () => void
  /** Usado quando não há pedido identificado */
  onBack?: () => void
}

export default function SendDocuments({ orderId, step, onNavigateToStep, onComplete, onBack }: SendDocumentsProps) {
  const navigation = useMemo(
    () => ({
      toOtherDocuments: () => onNavigateToStep('pessoais'),
      toLaudoMedico: () => onNavigateToStep('laudo'),
      toAnvisa: () => onNavigateToStep('anvisa'),
      toOrderDetails: onComplete,
      toCart: () => onBack?.()
    }),
    [onNavigateToStep, onComplete, onBack]
  )

  // O hook mantém as regras de qual documento pedir e para onde ir em cada etapa;
  // aqui só trocamos o destino das transições por navegação em query string.
  const props = useSendDocuments(undefined, {
    orderId,
    navigation,
    automaticReadingDefault: false,
    alwaysRequireLaudo: true,
    useAssociationDocumentRoutes: true
  })

  if (!orderId) {
    return (
      <CartContainer>
        <Result
          icon={<img src={NoToken} alt="Sem token" />}
          title="Nenhum pedido foi identificado."
          extra={
            <Button onClick={onBack}>
              Voltar para <strong>meus pedidos</strong>
            </Button>
          }
        />
      </CartContainer>
    )
  }

  const stepProps = {
    ...(props as any),
    // Sem esta prop as UIs redirecionam para /painel-do-usuario e tiram o usuário do painel de associados
    onInvalidOrderStatus: onComplete,
    // StepsSendDocuments só renderiza o toggle de leitura automática quando recebe o handler
    setAutomaticReadingEnabled: undefined
  }

  const renderStep = () => {
    switch (step) {
      case 'pessoais':
        return <SendImportDocumentsUI {...stepProps} />
      case 'laudo':
        return <SendLaudoMedicoUI {...stepProps} />
      case 'anvisa':
        return <SendAnvisaDocumentUI {...stepProps} />
      case 'receita':
      default:
        return <SendPrescripitonDocUI {...stepProps} />
    }
  }

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>{STEP_TITLE[step]}</CartTitle>
      </CartHeader>
      {renderStep()}
    </CartContainer>
  )
}

export { getOrderFlowRules } from './flowRules'
